// ─── Role Definitions ─────────────────────────────────────────────────────────

export type AppRole =
  | 'Procurement Officer' |'IT Security' |'Privacy Officer' |'City Attorney' |'Finance Director' |'CISO' |'Admin'; // full access

// ─── Route Permissions ────────────────────────────────────────────────────────
// Maps each role to the set of routes it can access.
// Routes NOT in the list will redirect to /executive-dashboard.

export const ROLE_ALLOWED_ROUTES: Record<AppRole, string[]> = {
  'Procurement Officer': [
    '/executive-dashboard',
    '/vendor-management',
    '/vendor-workspace',
    '/intake-form',
    '/sourcing-workspace',
    '/due-diligence-workspace',
    '/monitoring',
    '/contracts',
    '/analytics',
    '/reports',
    '/notifications',
  ],
  'IT Security': [
    '/executive-dashboard',
    '/vendor-management',
    '/vendor-workspace',
    '/due-diligence-workspace',
    '/monitoring',
    '/issues',
    '/contracts',
    '/analytics',
    '/reports',
    '/notifications',
  ],
  'Privacy Officer': [
    '/executive-dashboard',
    '/vendor-management',
    '/vendor-workspace',
    '/due-diligence-workspace',
    '/monitoring',
    '/issues',
    '/contracts',
    '/analytics',
    '/reports',
    '/notifications',
  ],
  'City Attorney': [
    '/executive-dashboard',
    '/vendor-management',
    '/vendor-workspace',
    '/sourcing-workspace',
    '/due-diligence-workspace',
    '/contracts',
    '/analytics',
    '/reports',
    '/notifications',
  ],
  'Finance Director': [
    '/executive-dashboard',
    '/vendor-management',
    '/vendor-workspace',
    '/sourcing-workspace',
    '/monitoring',
    '/contracts',
    '/analytics',
    '/reports',
    '/notifications',
  ],
  CISO: [
    '/executive-dashboard',
    '/vendor-management',
    '/vendor-workspace',
    '/due-diligence-workspace',
    '/monitoring',
    '/issues',
    '/sourcing-workspace',
    '/contracts',
    '/analytics',
    '/reports',
    '/notifications',
  ],
  Admin: [
    '/executive-dashboard',
    '/vendor-management',
    '/vendor-workspace',
    '/intake-form',
    '/sourcing-workspace',
    '/due-diligence-workspace',
    '/monitoring',
    '/issues',
    '/contracts',
    '/analytics',
    '/reports',
    '/notifications',
  ],
};

// ─── Action Permissions ───────────────────────────────────────────────────────
// Fine-grained action keys. Each role lists the actions it is ALLOWED to perform.

export type AppAction =
  | 'vendor:create'       // "+ New Vendor" button
  | 'vendor:edit'         // Edit vendor details
  | 'vendor:delete'       // Delete / archive vendor
  | 'vendor:flag'         // Flag vendor for review
  | 'vendor:export'       // Export vendor list
  | 'vendor:bulkAction'   // Bulk actions on vendor table
  | 'issue:create'        // Create new issue
  | 'issue:advance'       // Advance issue status
  | 'issue:escalate'      // Escalate issue
  | 'issue:reassign'      // Reassign issue owner
  | 'issue:close'         // Close an issue
  | 'intake:submit'       // Submit intake form
  | 'sourcing:createRFP'  // Create new RFP
  | 'sourcing:awardRFP'   // Award an RFP
  | 'sourcing:editScores' // Edit evaluation scores
  | 'dd:approve'          // Approve DD review
  | 'dd:reject'           // Reject DD review
  | 'dd:upload'           // Upload DD evidence
  | 'monitoring:remind'   // Send reminder for missing evidence
  | 'monitoring:upload'   // Upload monitoring evidence
  | 'dashboard:export'    // Export dashboard report
  | 'dashboard:newVendor' // New Vendor from dashboard header
  | 'admin:access';       // Access admin settings

export const ROLE_ALLOWED_ACTIONS: Record<AppRole, AppAction[]> = {
  'Procurement Officer': [
    'vendor:create',
    'vendor:edit',
    'vendor:flag',
    'vendor:export',
    'vendor:bulkAction',
    'intake:submit',
    'sourcing:createRFP',
    'sourcing:awardRFP',
    'sourcing:editScores',
    'dd:upload',
    'monitoring:remind',
    'monitoring:upload',
    'dashboard:export',
    'dashboard:newVendor',
  ],
  'IT Security': [
    'vendor:flag',
    'vendor:export',
    'issue:create',
    'issue:advance',
    'issue:escalate',
    'issue:reassign',
    'dd:upload',
    'dd:approve',
    'dd:reject',
    'monitoring:remind',
    'monitoring:upload',
    'dashboard:export',
  ],
  'Privacy Officer': [
    'vendor:flag',
    'vendor:export',
    'issue:create',
    'issue:advance',
    'issue:escalate',
    'dd:upload',
    'monitoring:remind',
    'monitoring:upload',
    'dashboard:export',
  ],
  'City Attorney': [
    'vendor:flag',
    'vendor:export',
    'sourcing:editScores',
    'dd:approve',
    'dd:reject',
    'dd:upload',
    'dashboard:export',
  ],
  'Finance Director': [
    'vendor:export',
    'sourcing:editScores',
    'monitoring:remind',
    'dashboard:export',
  ],
  CISO: [
    'vendor:create',
    'vendor:edit',
    'vendor:flag',
    'vendor:export',
    'vendor:bulkAction',
    'vendor:delete',
    'issue:create',
    'issue:advance',
    'issue:escalate',
    'issue:reassign',
    'issue:close',
    'sourcing:createRFP',
    'sourcing:awardRFP',
    'sourcing:editScores',
    'dd:approve',
    'dd:reject',
    'dd:upload',
    'monitoring:remind',
    'monitoring:upload',
    'dashboard:export',
    'dashboard:newVendor',
  ],
  Admin: [
    'vendor:create',
    'vendor:edit',
    'vendor:delete',
    'vendor:flag',
    'vendor:export',
    'vendor:bulkAction',
    'issue:create',
    'issue:advance',
    'issue:escalate',
    'issue:reassign',
    'issue:close',
    'intake:submit',
    'sourcing:createRFP',
    'sourcing:awardRFP',
    'sourcing:editScores',
    'dd:approve',
    'dd:reject',
    'dd:upload',
    'monitoring:remind',
    'monitoring:upload',
    'dashboard:export',
    'dashboard:newVendor',
    'admin:access',
  ],
};

// ─── Helper Functions ─────────────────────────────────────────────────────────

export function canAccessRoute(role: AppRole, pathname: string): boolean {
  const allowed = ROLE_ALLOWED_ROUTES[role] ?? [];
  return allowed.some((r) => pathname === r || pathname.startsWith(r + '/'));
}

export function canPerformAction(role: AppRole, action: AppAction): boolean {
  const allowed = ROLE_ALLOWED_ACTIONS[role] ?? [];
  return allowed.includes(action);
}

// ─── Role Display Metadata ────────────────────────────────────────────────────

export const ROLE_META: Record<AppRole, { color: string; initials: string }> = {
  'Procurement Officer': { color: 'bg-teal-600', initials: 'PO' },
  'IT Security':         { color: 'bg-blue-600', initials: 'IT' },
  'Privacy Officer':     { color: 'bg-purple-600', initials: 'PR' },
  'City Attorney':       { color: 'bg-amber-600', initials: 'CA' },
  'Finance Director':    { color: 'bg-green-600', initials: 'FD' },
  CISO:                  { color: 'bg-red-700', initials: 'CI' },
  Admin:                 { color: 'bg-slate-700', initials: 'AD' },
};
